// Opens the tutor name poll after a call, or in a review once the learner has answered and chatted (the two
// moments the owner chose, 2026-11-04). Startup code (the call surfaces, the review's navigation, the
// answer recorder) imports this, so it stays tiny: no modal, no catalog, no network. The modal itself is
// lazy-loaded by DeferredGlobalModals.
import { create } from "zustand";
import { TUTOR_NAME_POLL_ID } from "./tutorNamePollOptions";
import { getVoiceCallStartCount } from "../../voice/core/voiceCallStarts";
import type { UserData } from "../../constants/GlobalTypesAndEnumsAndConstants";

export type TutorNamePollTrigger = "call_hang_up" | "review_next_question";

// What opened the poll, for its analytics (content-free).
export interface TutorNamePollCallContext {
  trigger: TutorNamePollTrigger;
  surface: string;
  // The call that just ended; null when a review opened the poll
  connectedDurationSeconds: number | null;
  // The review that opened it: questions answered and chat messages sent in it so far
  reviewQuestionsAnswered?: number;
  reviewChatMessagesSent?: number;
  // Voice calls started in this page load when the poll was offered: if another call starts before the
  // poll can open (it waits on the Worker), the poll stays closed rather than covering the live call.
  voiceCallStartCountAtOffer: number;
}

/** Whether a voice call has started since this poll was offered (the poll must not cover it). */
export function hasVoiceCallStartedSinceOffer(callContext: TutorNamePollCallContext): boolean {
  return getVoiceCallStartCount() !== callContext.voiceCallStartCountAtOffer;
}

interface TutorNamePollStoreState {
  callContext: TutorNamePollCallContext | null;
  openAfterCall: (callContext: TutorNamePollCallContext) => void;
  close: () => void;
}

export const useTutorNamePollStore = create<TutorNamePollStoreState>((set) => ({
  callContext: null,
  openAfterCall: (callContext) => set({ callContext }),
  close: () => set({ callContext: null }),
}));

// A call shorter than this is a misdial or a quick test; asking to name the tutor then would feel random.
export const TUTOR_NAME_POLL_MINIMUM_CALL_SECONDS = 30;

// The owner's rule (2026-11-04): a learner is asked once. After the modal has shown, whether they voted or
// closed it, it never shows again for that account. Three records enforce it: this device's (immediate,
// survives a reload), the account's `user_settings.tutor_name_poll_shown_at[pollId]` (other devices), and
// the Worker's vote (a learner who voted anywhere).
function deviceRecordKey(userId: string): string {
  return `tutor_name_poll:${TUTOR_NAME_POLL_ID}:${userId}`;
}

export function hasThisDeviceAskedTutorNamePoll(userId: string): boolean {
  try {
    return JSON.parse(localStorage.getItem(deviceRecordKey(userId)) ?? "null")?.hasBeenAsked === true;
  } catch {
    return false;
  }
}

export function rememberTutorNamePollAskedOnThisDevice(userId: string): void {
  try {
    localStorage.setItem(deviceRecordKey(userId), JSON.stringify({ hasBeenAsked: true }));
  } catch {
    // Storage full or blocked: the account record still keeps it from showing again.
  }
}

/** Whether this account was already shown the poll, on any device (the account record). */
export function hasAccountBeenAskedTutorNamePoll(user: Pick<UserData, "user_settings"> | null | undefined): boolean {
  const shownAt = user?.user_settings?.tutor_name_poll_shown_at;
  return Boolean(shownAt && typeof shownAt === "object" && (shownAt as Record<string, unknown>)[TUTOR_NAME_POLL_ID]);
}

/**
 * Called when a learner hangs up an ordinary tutor call (not a feedback call, a challenge or a clinical
 * station, and not a call the free limit or the idle timer ended). Opens the poll unless the call was too
 * short or this learner was already asked. The container then asks the Worker, which has the final word on
 * whether this account already voted.
 */
export function offerTutorNamePollAfterCall(args: {
  user: Pick<UserData, "id" | "user_settings"> | null | undefined;
  surface: string;
  connectedDurationSeconds: number;
}): void {
  const userId = args.user?.id;
  if (!userId) return;
  if (args.connectedDurationSeconds < TUTOR_NAME_POLL_MINIMUM_CALL_SECONDS) return;
  if (hasThisDeviceAskedTutorNamePoll(userId) || hasAccountBeenAskedTutorNamePoll(args.user)) return;
  useTutorNamePollStore.getState().openAfterCall({
    trigger: "call_hang_up",
    surface: args.surface,
    connectedDurationSeconds: args.connectedDurationSeconds,
    voiceCallStartCountAtOffer: getVoiceCallStartCount(),
  });
}

// ─── in a review ───────────────────────────────────────────────────────────────
// Owner, 2026-11-04: in a review, once the learner has answered 3 or more questions and sent at least one
// message in the chat, the press that moves on to the next question opens the poll. Counted per review
// session (one visit to a set's review or the daily review, as ReviewQuestionsPage defines it), and only
// while that page is open, so a chat on another page never counts.
export const TUTOR_NAME_POLL_REVIEW_MINIMUM_ANSWERS = 3;
export const TUTOR_NAME_POLL_REVIEW_MINIMUM_CHAT_MESSAGES = 1;

const reviewSession = {
  isOpen: false,
  answeredCardIds: new Set<string>(),
  chatMessagesSent: 0,
};

export function startTutorNamePollReviewSession(): void {
  reviewSession.isOpen = true;
  reviewSession.answeredCardIds = new Set();
  reviewSession.chatMessagesSent = 0;
}

export function endTutorNamePollReviewSession(): void {
  reviewSession.isOpen = false;
}

/** A question answered in the open review (each question counts once, however many times it is answered). */
export function noteTutorNamePollReviewAnswer(cardId: string | null | undefined): void {
  if (reviewSession.isOpen && cardId) reviewSession.answeredCardIds.add(cardId);
}

/** A message the learner sent in the chat while a review is open (typed, a chip, or an "explain" button). */
export function noteTutorNamePollReviewChatMessage(): void {
  if (reviewSession.isOpen) reviewSession.chatMessagesSent += 1;
}

/**
 * Called on the press that moves a review on to the next question. Opens the poll when this review has 3+
 * answers and 1+ chat message, under the same once-per-account rule as after a call.
 */
export function offerTutorNamePollOnNextReviewQuestion(user: Pick<UserData, "id" | "user_settings"> | null | undefined): void {
  const userId = user?.id;
  if (!userId || !reviewSession.isOpen) return;
  if (reviewSession.answeredCardIds.size < TUTOR_NAME_POLL_REVIEW_MINIMUM_ANSWERS) return;
  if (reviewSession.chatMessagesSent < TUTOR_NAME_POLL_REVIEW_MINIMUM_CHAT_MESSAGES) return;
  if (useTutorNamePollStore.getState().callContext) return;
  if (hasThisDeviceAskedTutorNamePoll(userId) || hasAccountBeenAskedTutorNamePoll(user)) return;
  useTutorNamePollStore.getState().openAfterCall({
    trigger: "review_next_question",
    surface: "review",
    connectedDurationSeconds: null,
    reviewQuestionsAnswered: reviewSession.answeredCardIds.size,
    reviewChatMessagesSent: reviewSession.chatMessagesSent,
    voiceCallStartCountAtOffer: getVoiceCallStartCount(),
  });
}
